import React, { useEffect, useState, useRef } from 'react';
import {
  Layout, Typography, Card, Row, Col, Button, Space, Table, Modal, Form,
  Select, InputNumber, Input, Tag, Switch, Tooltip, Popconfirm, message, Alert, Divider, Badge
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, StarOutlined, CheckCircleOutlined,
  EditOutlined, DeleteOutlined, SettingOutlined, TrophyOutlined, ThunderboltOutlined,
  SlidersOutlined
} from '@ant-design/icons';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const DEFAULT_5_POINT_LEVELS = [
  { score: 5, minScore: 4.50, maxScore: 5.00, title: 'Outstanding', description: 'Consistently exceeds expectations in all performance criteria', minPct: 90, maxPct: 100, color: '#52c41a' },
  { score: 4, minScore: 3.75, maxScore: 4.49, title: 'Exceeds Expectations', description: 'Frequently exceeds requirements and achieves high standards', minPct: 75, maxPct: 89.99, color: '#1677ff' },
  { score: 3, minScore: 3.00, maxScore: 3.74, title: 'Meets Expectations', description: 'Fulfills position requirements and targets consistently', minPct: 60, maxPct: 74.99, color: '#fa8c16' },
  { score: 2, minScore: 2.00, maxScore: 2.99, title: 'Needs Improvement', description: 'Performance is below expectations in some areas', minPct: 40, maxPct: 59.99, color: '#faad14' },
  { score: 1, minScore: 0.00, maxScore: 1.99, title: 'Unsatisfactory', description: 'Consistently fails to meet basic position requirements', minPct: 0, maxPct: 39.99, color: '#f5222d' },
];

const DEFAULT_10_POINT_LEVELS = [
  { score: 10, title: 'Exceptional (10/10)', description: 'Pinnacle performance, industry benchmark', minPct: 95, maxPct: 100, color: '#722ed1' },
  { score: 9, title: 'Outstanding (9/10)', description: 'Superior execution and exceptional value addition', minPct: 88, maxPct: 94, color: '#52c41a' },
  { score: 8, title: 'Excellent (8/10)', description: 'Well above target metrics consistently', minPct: 80, maxPct: 87, color: '#389e0d' },
  { score: 7, title: 'Very Good (7/10)', description: 'Exceeds target metrics regularly', minPct: 72, maxPct: 79, color: '#1677ff' },
  { score: 6, title: 'Good (6/10)', description: 'Meets all core requirements successfully', minPct: 65, maxPct: 71, color: '#0958d9' },
  { score: 5, title: 'Average (5/10)', description: 'Fulfills basic duties with minor gaps', minPct: 55, maxPct: 64, color: '#fa8c16' },
  { score: 4, title: 'Fair (4/10)', description: 'Needs guidance in specific operational areas', minPct: 45, maxPct: 54, color: '#faad14' },
  { score: 3, title: 'Below Expectations (3/10)', description: 'Requires regular intervention and monitoring', minPct: 35, maxPct: 44, color: '#d4b106' },
  { score: 2, title: 'Poor (2/10)', description: 'Substantial improvement mandatory', minPct: 20, maxPct: 34, color: '#ff4d4f' },
  { score: 1, title: 'Critical Concern (1/10)', description: 'Unsatisfactory performance across all targets', minPct: 0, maxPct: 19, color: '#cf1322' },
];

const DEFAULT_100_POINT_LEVELS = [
  { score: 100, title: 'Grade A+ (90-100%)', description: 'Outstanding / Exceptional Performance', minPct: 90, maxPct: 100, color: '#52c41a' },
  { score: 80, title: 'Grade A (75-89%)', description: 'Exceeds Expectations', minPct: 75, maxPct: 89, color: '#1677ff' },
  { score: 65, title: 'Grade B (60-74%)', description: 'Meets Expectations', minPct: 60, maxPct: 74, color: '#fa8c16' },
  { score: 50, title: 'Grade C (40-59%)', description: 'Needs Improvement', minPct: 40, maxPct: 59, color: '#faad14' },
  { score: 30, title: 'Grade D (0-39%)', description: 'Unsatisfactory', minPct: 0, maxPct: 39, color: '#f5222d' },
];

export default function RatingSystem() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scales, setScales] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingScale, setEditingScale] = useState(null);
  const [levelsList, setLevelsList] = useState(DEFAULT_5_POINT_LEVELS);
  const [form] = Form.useForm();
  const levelsContainerRef = useRef(null);

  const fetchScales = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pms/rating-scales');
      if (res.data?.success) {
        setScales(res.data.scales || []);
      } else {
        message.error(res.data?.message || 'Failed to load rating scales');
      }
    } catch (e) {
      console.error('Fetch rating scales error:', e);
      message.error(e.response?.data?.message || 'Failed to load rating scales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScales();
  }, []);

  const resetScrollToTop = () => {
    setTimeout(() => {
      if (levelsContainerRef.current) {
        levelsContainerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const handleOpenModal = (scaleObj = null) => {
    setEditingScale(scaleObj);
    if (scaleObj) {
      setLevelsList(Array.isArray(scaleObj.levels) ? scaleObj.levels : DEFAULT_5_POINT_LEVELS);
      form.setFieldsValue({
        scaleName: scaleObj.scaleName,
        scaleType: scaleObj.scaleType || '5-Point',
        maxPoints: scaleObj.maxPoints !== undefined ? Number(scaleObj.maxPoints) : 5,
        description: scaleObj.description || '',
        isDefault: !!scaleObj.isDefault,
      });
    } else {
      form.resetFields();
      setLevelsList(DEFAULT_5_POINT_LEVELS);
      form.setFieldsValue({
        scaleName: '',
        scaleType: '5-Point',
        maxPoints: 5,
        isDefault: scales.length === 0,
      });
    }
    setIsModalOpen(true);
    resetScrollToTop();
  };

  const handlePresetSelect = (presetType) => {
    if (presetType === '5-Point') {
      setLevelsList(DEFAULT_5_POINT_LEVELS);
      form.setFieldsValue({ scaleType: '5-Point', maxPoints: 5 });
    } else if (presetType === '10-Point') {
      setLevelsList(DEFAULT_10_POINT_LEVELS);
      form.setFieldsValue({ scaleType: '10-Point', maxPoints: 10 });
    } else if (presetType === '100-Point' || (presetType && presetType.includes('100'))) {
      setLevelsList(DEFAULT_100_POINT_LEVELS);
      form.setFieldsValue({ scaleType: '100-Point', maxPoints: 100 });
    } else if (presetType === 'Custom') {
      form.setFieldsValue({ scaleType: 'Custom' });
    }
    resetScrollToTop();
  };

  const handleAddLevel = () => {
    const nextScore = levelsList.length > 0 ? (levelsList[0].score || 10) + 10 : 10;
    setLevelsList([
      { score: nextScore, title: `Level ${nextScore}`, description: 'Custom level requirement', minPct: 0, maxPct: 100, color: '#1677ff' },
      ...levelsList
    ]);
    resetScrollToTop();
  };

  const handleRemoveLevel = (index) => {
    const updated = levelsList.filter((_, idx) => idx !== index);
    setLevelsList(updated);
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        scaleName: values.scaleName,
        scaleType: values.scaleType || 'Custom',
        maxPoints: values.maxPoints !== undefined ? Number(values.maxPoints) : 5,
        description: values.description || null,
        isDefault: !!values.isDefault,
        levels: levelsList,
      };

      if (editingScale) {
        const res = await api.put(`/admin/pms/rating-scales/${editingScale.id}`, payload);
        if (res.data?.success) {
          message.success('Rating Scale updated successfully');
          setIsModalOpen(false);
          fetchScales();
        } else {
          message.error(res.data?.message || 'Failed to update rating scale');
        }
      } else {
        const res = await api.post('/admin/pms/rating-scales', payload);
        if (res.data?.success) {
          message.success('Rating Scale created successfully');
          setIsModalOpen(false);
          fetchScales();
        } else {
          message.error(res.data?.message || 'Failed to create rating scale');
        }
      }
    } catch (e) {
      console.error('Save rating scale error:', e);
      message.error(e.response?.data?.message || 'Failed to save rating scale');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const res = await api.post(`/admin/pms/rating-scales/${id}/set-default`);
      if (res.data?.success) {
        message.success(res.data.message || 'Default rating scale updated');
        fetchScales();
      } else {
        message.error(res.data?.message || 'Failed setting default scale');
      }
    } catch (e) {
      console.error('Set default scale error:', e);
      message.error(e.response?.data?.message || 'Failed setting default scale');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/admin/pms/rating-scales/${id}`);
      if (res.data?.success) {
        message.success('Rating Scale deleted successfully');
        fetchScales();
      } else {
        message.error(res.data?.message || 'Failed to delete scale');
      }
    } catch (e) {
      console.error('Delete scale error:', e);
      message.error(e.response?.data?.message || 'Failed to delete scale');
    }
  };

  const updateLevelItem = (index, key, val) => {
    const updated = [...levelsList];
    updated[index] = { ...updated[index], [key]: val };
    setLevelsList(updated);
  };

  const columns = [
    {
      title: 'Rating Scale Name',
      key: 'scaleName',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b' }}>
            <StarOutlined style={{ marginRight: '8px', color: '#faad14' }} />
            {record.scaleName}
            {record.isDefault && (
              <Tag color="gold" style={{ marginLeft: '10px', borderRadius: '10px', fontWeight: '700' }}>
                SYSTEM DEFAULT
              </Tag>
            )}
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Scale Type',
      dataIndex: 'scaleType',
      key: 'scaleType',
      render: (val) => <Tag color="blue" style={{ borderRadius: '10px', fontWeight: '600' }}>{val || 'Custom'}</Tag>,
    },
    {
      title: 'Max Score / Points',
      dataIndex: 'maxPoints',
      key: 'maxPoints',
      render: (val) => (
        <span style={{ fontWeight: '700', fontSize: '14px', color: '#1677ff' }}>
          {val ? `${val} Points` : '5 Points'}
        </span>
      ),
    },
    {
      title: 'Rating Levels Breakdown',
      key: 'levels',
      render: (_, record) => {
        const lvls = Array.isArray(record.levels) ? record.levels : [];
        return (
          <Space wrap size={[4, 8]}>
            {lvls.map((l, idx) => (
              <Tag
                key={idx}
                color={l.color || 'geekblue'}
                style={{ borderRadius: '8px', fontWeight: '600', padding: '2px 8px' }}
              >
                {l.score}: {l.title}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          {!record.isDefault && (
            <Tooltip title="Set as System Default">
              <Button
                type="text"
                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                onClick={() => handleSetDefault(record.id)}
              >
                Set Default
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Edit Scale">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1677ff' }} />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Rating Scale?"
            description="Are you sure you want to delete this rating scale?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Scale">
              <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden', transition: 'margin-left 0.2s' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Performance Management - Rating System (Section 11)" />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* Header Cards */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL RATING SCALES</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginTop: '4px' }}>{scales.length}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>ACTIVE DEFAULT SCALE</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1677ff', marginTop: '4px' }}>
                    {scales.find(s => s.isDefault)?.scaleName || (scales.length > 0 ? scales[0].scaleName : 'No Scale Configured')}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>CONFIGURABLE TYPES</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#52c41a', marginTop: '6px' }}>
                    5-Point, 10-Point, 100-Point & Custom Scales
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Main Content Card */}
            <Card
              title={<span style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>Configured Rating Scales</span>}
              extra={
                <Space wrap>
                  <Button icon={<ReloadOutlined />} onClick={fetchScales} />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    shape="round"
                    onClick={() => handleOpenModal()}
                  >
                    Add Rating Scale
                  </Button>
                </Space>
              }
              style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
            >
              <Table
                columns={columns}
                dataSource={scales}
                rowKey="id"
                loading={loading}
                pagination={false}
              />
            </Card>

            {/* Active Default Scale Preview Breakdown */}
            {scales.length > 0 && (
              <Card
                title={
                  <span>
                    <TrophyOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                    Default Rating Scale Breakdown
                  </span>
                }
                style={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}
              >
                {(() => {
                  const defaultScale = scales.find(s => s.isDefault) || scales[0];
                  const levels = Array.isArray(defaultScale.levels) ? defaultScale.levels : [];
                  return (
                    <Row gutter={[16, 16]}>
                      {levels.map((lvl, idx) => (
                        <Col xs={24} sm={12} md={8} lg={4.8} key={idx}>
                          <Card
                            bodyStyle={{ padding: '16px' }}
                            style={{
                              borderRadius: '12px',
                              borderLeft: `4px solid ${lvl.color || '#1677ff'}`,
                              background: '#fafafa',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '18px', fontWeight: '800', color: lvl.color || '#1677ff' }}>
                                Score {lvl.score}
                              </span>
                              <Tag color={lvl.color || 'blue'} style={{ fontWeight: 'bold' }}>
                                {lvl.minScore !== undefined && lvl.maxScore !== undefined
                                  ? `${Number(lvl.minScore).toFixed(2)} – ${Number(lvl.maxScore).toFixed(2)}`
                                  : `${lvl.minPct}-${lvl.maxPct}%`}
                              </Tag>
                            </div>
                            <div style={{ fontWeight: '700', color: '#1e293b', marginTop: '6px', fontSize: '14px' }}>
                              {lvl.title}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                              {lvl.description || 'Standard metric benchmark'}
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  );
                })()}
              </Card>
            )}

          </Space>
        </Content>
      </Layout>

      {/* Add / Edit Rating Scale Modal - Upgraded to Spacious Card Design */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 4, height: 22, background: '#fa8c16', borderRadius: 2 }} />
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              {editingScale ? 'Edit Rating Scale Configuration' : 'Create New Rating Scale'}
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={1000}
        destroyOnClose
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '16px' }}>
          
          {/* Card 1: Scale Definition & Type */}
          <Card
            type="inner"
            title={
              <Space>
                <StarOutlined style={{ color: '#fa8c16' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Scale Configuration & Scheme</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="scaleName"
                  label={<strong>Rating Scale Name</strong>}
                  rules={[{ required: true, message: 'Enter Scale Name' }]}
                >
                  <Input placeholder="e.g. Standard 5-Point Rating Scale or 100-Point Scale" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="scaleType" label={<strong>Scale Type Preset</strong>} rules={[{ required: true }]}>
                  <Select onChange={(val) => handlePresetSelect(val)}>
                    <Option value="5-Point">5-Point Scale</Option>
                    <Option value="10-Point">10-Point Scale (1-10)</Option>
                    <Option value="100-Point">100-Point Scale (0-100)</Option>
                    <Option value="Custom">Custom Scale</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="maxPoints" label={<strong>Max Score / Points</strong>} rules={[{ required: true }]}>
                  <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={18}>
                <Form.Item name="description" label={<strong>Description / Guidelines</strong>} style={{ marginBottom: 0 }}>
                  <Input placeholder="Guidelines for HR & Managers on rating methodology and standards..." />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={<strong>Default Rating Scale</strong>} style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                    <Form.Item name="isDefault" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Default" unCheckedChildren="No" />
                    </Form.Item>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 2: Rating Levels & Score Brackets */}
          <Card
            type="inner"
            title={
              <Space>
                <SlidersOutlined style={{ color: '#1677ff' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>
                  Rating Levels & Score Bracket Definitions ({levelsList.length} Levels)
                </span>
              </Space>
            }
            extra={
              <Space>
                <Button size="small" onClick={() => handlePresetSelect('5-Point')}>5-Point Preset</Button>
                <Button size="small" onClick={() => handlePresetSelect('10-Point')}>10-Point Preset</Button>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddLevel}>
                  Add Custom Level
                </Button>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <div
              ref={levelsContainerRef}
              style={{ maxHeight: '340px', overflowY: 'auto', paddingRight: '6px', border: '1px solid #e2e8f0', padding: '10px', borderRadius: '8px', background: '#ffffff' }}
            >
              {levelsList.map((level, idx) => (
                <Row gutter={8} key={idx} style={{ marginBottom: '10px', alignItems: 'center', background: '#fafafa', padding: '8px 12px', borderRadius: '8px', border: '1px solid #eeeff2' }}>
                  <Col span={2}>
                    <Text strong style={{ fontSize: '11px', color: '#64748b' }}>Score</Text>
                    <InputNumber
                      size="small"
                      value={level.score}
                      onChange={(val) => updateLevelItem(idx, 'score', val)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <Text strong style={{ fontSize: '11px', color: '#64748b' }}>Min Score</Text>
                    <InputNumber
                      size="small"
                      step={0.01}
                      value={level.minScore !== undefined ? level.minScore : (level.score ? level.score - 0.5 : 0)}
                      onChange={(val) => updateLevelItem(idx, 'minScore', val)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <Text strong style={{ fontSize: '11px', color: '#64748b' }}>Max Score</Text>
                    <InputNumber
                      size="small"
                      step={0.01}
                      value={level.maxScore !== undefined ? level.maxScore : (level.score || 5)}
                      onChange={(val) => updateLevelItem(idx, 'maxScore', val)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Text strong style={{ fontSize: '11px', color: '#64748b' }}>Rating Category Title</Text>
                    <Input
                      size="small"
                      value={level.title}
                      placeholder="e.g. Outstanding"
                      onChange={(e) => updateLevelItem(idx, 'title', e.target.value)}
                    />
                  </Col>
                  <Col span={8}>
                    <Text strong style={{ fontSize: '11px', color: '#64748b' }}>Description / Rubric</Text>
                    <Input
                      size="small"
                      value={level.description}
                      placeholder="Performance criteria explanation"
                      onChange={(e) => updateLevelItem(idx, 'description', e.target.value)}
                    />
                  </Col>
                  <Col span={2} style={{ textAlign: 'right' }}>
                    <Text strong style={{ fontSize: '11px', display: 'block', color: '#ff4d4f' }}>Del</Text>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveLevel(idx)}
                    />
                  </Col>
                </Row>
              ))}
            </div>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button size="large" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button size="large" type="primary" htmlType="submit">
              {editingScale ? 'Update Rating Scale' : 'Create Rating Scale'}
            </Button>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}
